import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

import { ItineraryView, PassengerLegStatusEnum } from '@mopla/data-models';
import { checkIsAnyWalkLeg, getItineraryTimingV2 } from '@mopla/utils';

import { Chip } from '../../atoms/Chip/Chip';

export interface IBookingStatusBadgeProps {
	itinerary: ItineraryView;
}

const ChipStyled = styled(Chip)(({ theme }) => ({
	minWidth: 65,
	'&.bookingChipScheduledCompleted': {
		color: theme.palette.primary.dark,
		backgroundColor: theme.palette.secondary.main,
	},
	'&.bookingChipDelayed': {
		color: theme.palette.primary.dark,
		backgroundColor: theme.palette.warning.main,
	},
	'&.bookingChipCancelled': {
		color: theme.palette.neutral[0],
		backgroundColor: theme.palette.error.main,
	},
	'&.bookingChipArrived': {
		color: theme.palette.neutral[0],
		backgroundColor: theme.palette.myway?.main ?? '#608DF0',
	},
	'&.bookingChipOnTheWay': {
		color: theme.palette.primary.main,
		backgroundColor: theme.palette.primary.light,
	},
	'&.bookingChipCheckedIn': {
		color: theme.palette.primary.main,
		backgroundColor: theme.palette.neutral[40],
		border: `1px solid ${theme.palette.primary.main}`,
	},
}));

export const BookingStatusBadge: FC<IBookingStatusBadgeProps> = ({
	itinerary,
}) => {
	const { t } = useTranslation('tickets');

	const firstLegStatus = useMemo(
		() => itinerary.legs.filter((leg) => !checkIsAnyWalkLeg(leg))[0].legStatus,
		[itinerary]
	);

	const itineraryTimings = useMemo(
		() => getItineraryTimingV2(itinerary.legs),
		[itinerary]
	);

	if (firstLegStatus === PassengerLegStatusEnum.ON_THE_WAY) {
		return (
			<ChipStyled className="bookingChipOnTheWay">
				{t('text.on_the_way_ride')}
			</ChipStyled>
		);
	}

	if (firstLegStatus === PassengerLegStatusEnum.DRIVER_ARRIVED) {
		return (
			<ChipStyled className="bookingChipArrived">
				{t('text.arrived_ride')}
			</ChipStyled>
		);
	}

	if (firstLegStatus === PassengerLegStatusEnum.COMPLETED) {
		return (
			<ChipStyled className="bookingChipScheduledCompleted">
				{t('text.completed_ride')}
			</ChipStyled>
		);
	}

	if (firstLegStatus === PassengerLegStatusEnum.CANCELLED) {
		return (
			<ChipStyled className="bookingChipCancelled">
				{t('text.cancelled_ride')}
			</ChipStyled>
		);
	}

	if (itineraryTimings.isDelayed) {
		return (
			<ChipStyled className="bookingChipDelayed">
				{t('text.delayed_ride')}
			</ChipStyled>
		);
	}

	if (firstLegStatus === PassengerLegStatusEnum.SCHEDULED) {
		return (
			<ChipStyled className="bookingChipScheduledCompleted">
				{t('text.fixed_ride')}
			</ChipStyled>
		);
	}

	if (firstLegStatus === PassengerLegStatusEnum.CHECKED_IN) {
		return (
			<ChipStyled className="bookingChipCheckedIn">
				{t('text.checked_in_ride')}
			</ChipStyled>
		);
	}

	//Do not return anything if status is BOOKED
	return null;
};
